import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Box,
  Stack,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { PUBLISH_ROUTINE, UPDATE_SHARED_ROUTINE, SHARED_ROUTINES } from 'api/graphql/play';
import contentApi from 'api/graphql/content';
import { createSnack } from 'common/utils/general';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import uiSlice from 'store/modules/ui';
import { MUSCLE_GROUPS, muscleLabel } from 'components/play/constants';
import BlockNoteContentEditor from 'components/shared/content/BlockNoteContentEditor';

const initials = (name) => (name || '?').trim().charAt(0).toUpperCase();

/**
 * OTRIX Play — compose / edit a forum post (Facebook-style): author header, rich BlockNote
 * description (slash "/" menu, con imágenes y video) y un selector de enfoque muscular. No hay
 * campo de título: la descripción rica lo contiene. Publica la rutina semanal activa como
 * snapshot, o edita una publicación existente cuando llega `routine`.
 *
 * `initialDescription` permite abrirlo con la descripción ya armada (p. ej. "Compartir" de
 * Mi Rango, que trae el resumen de la semana listo) — sigue siendo una publicación NUEVA.
 */
function PublishRoutineDialog({
  open,
  onClose,
  routine = null,
  feedVars = null,
  initialDescription = ''
}) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const me = useSelector((s) => s.auth.user);

  const editing = Boolean(routine);
  const [description, setDescription] = useState('');
  const [muscleFocus, setMuscleFocus] = useState('');
  // Adjuntar la semana es lo esperado al "publicar mi rutina", pero se puede apagar para
  // compartir solo un pensamiento: ese post no lleva días ni se puede adoptar.
  const [attachRoutine, setAttachRoutine] = useState(true);

  useEffect(() => {
    if (!open) return;
    setDescription(routine?.description || initialDescription || '');
    setMuscleFocus(routine?.muscle_focus || '');
    setAttachRoutine(true);
  }, [open, routine, initialDescription]);

  // Refetch de la query REALMENTE montada en el feed (con su filtro de enfoque activo);
  // si no viene, cae al feed sin filtro.
  const refetch = [{ query: SHARED_ROUTINES, variables: feedVars || { muscle_focus: undefined } }];
  const [publish, { loading: publishing }] = useMutation(PUBLISH_ROUTINE, { refetchQueries: refetch });
  const [update, { loading: updating }] = useMutation(UPDATE_SHARED_ROUTINE, { refetchQueries: refetch });
  const loading = publishing || updating;

  const snack = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `pub-${Date.now()}`, variant, message })));

  const handleSubmit = async () => {
    try {
      // name va null: el backend deriva un nombre corto de la descripción (para admin/moderación).
      const variables = { name: null, description: description || null, muscle_focus: muscleFocus || null };
      if (editing) {
        // Editar solo toca el texto: el snapshot de la rutina es inmutable.
        await update({ variables: { id: routine.id, ...variables } });
        snack('success', t('publish.updated', { ns: 'play', defaultValue: 'Publicación actualizada' }));
      } else {
        await publish({ variables: { ...variables, attach_routine: attachRoutine } });
        snack('success', t('publish.success', { ns: 'play' }));
      }
      onClose();
    } catch {
      snack('error', t('publish.error', { ns: 'play' }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Typography variant="h6" fontWeight={800}>
          {editing ? t('publish.editTitle', { ns: 'play', defaultValue: 'Editar publicación' }) : t('publish.title', { ns: 'play' })}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1, mb: 2 }}>
          <Avatar src={me?.photo || undefined} sx={{ width: 44, height: 44, bgcolor: 'gold.soft', color: 'gold.main' }}>
            {initials(me?.full_name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={800} noWrap>
              {me?.full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('publish.subtitle', { ns: 'play', defaultValue: 'Comparte tu rutina de la semana con la comunidad.' })}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('publish.description', { ns: 'play' })}
            </Typography>
            <BlockNoteContentEditor
              value={description}
              onChange={setDescription}
              minHeight={220}
              uploadFn={contentApi.uploadPlayMedia}
            />
          </Box>

          <TextField select label={t('publish.focus', { ns: 'play' })} value={muscleFocus} onChange={(e) => setMuscleFocus(e.target.value)} fullWidth>
            <MenuItem value="">{t('publish.noFocus', { ns: 'play' })}</MenuItem>
            {MUSCLE_GROUPS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {muscleLabel(t, m.value)}
              </MenuItem>
            ))}
          </TextField>

          {/* Al editar no aplica: el snapshot de la rutina ya quedó fijo al publicar. */}
          {!editing && (
            <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', bgcolor: 'surface.raised' }}>
              <FormControlLabel
                control={<Switch checked={attachRoutine} onChange={(e) => setAttachRoutine(e.target.checked)} color="secondary" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      {t('publish.attachRoutine', { ns: 'play' })}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {attachRoutine
                        ? t('publish.attachRoutineOn', { ns: 'play' })
                        : t('publish.attachRoutineOff', { ns: 'play' })}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, alignItems: 'flex-start' }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}>
          {t('publish.cancel', { ns: 'play' })}
        </Button>
        <Button onClick={handleSubmit} disabled={loading || !description} sx={{ ...premiumButtonSx, px: 3, py: 1 }}>
          {editing ? t('publish.save', { ns: 'play', defaultValue: 'Guardar' }) : t('publish.publish', { ns: 'play' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PublishRoutineDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  routine: PropTypes.object,
  // Variables del feed montado (para que el refetch tras publicar refresque la lista visible).
  feedVars: PropTypes.object,
  // HTML pre-armado para una publicación nueva (Compartir de Mi Rango).
  initialDescription: PropTypes.string
};

export default PublishRoutineDialog;
